class LazyVideo extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.video = window.BAO.Utils.getElement('video', { context: this })

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })
    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video)
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    if (this.video.dataset?.poster) {
      this.video.setAttribute('poster', this.video.dataset.poster)
    }

    for (const source in this.video.children) {
      const videoSource = this.video.children[source]

      if (
        typeof videoSource.tagName === 'string' &&
        videoSource.tagName === 'SOURCE'
      ) {
        videoSource.src = videoSource.dataset.src
      }
    }

    this.video.load()
    observer.unobserve(this.video)

    this.setAttribute('loaded', '')

    if (this.closest('[data-paused="true"]')) {
      this.video.pause()
      return
    }

    if (
      window.BAO.MediaBreakpoints.isSm ||
      this.video.hasAttribute('autoplay') === false
    ) {
      return
    }

    if (this.video.paused) {
      document.addEventListener('touchstart', () => {
        if (this.video.paused) {
          this.video.play()
        }
      })
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}

class LazyVideoControls extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.play?.element) {
      this.handlePlayClick = this.handlePlayClick.bind(this)
      this.listeners.add(this.els.play.element, 'click', this.handlePlayClick)
    }

    if (this.els.unmute?.element) {
      this.handleUnmuteClick = this.handleUnmuteClick.bind(this)
      this.listeners.add(
        this.els.unmute.element,
        'click',
        this.handleUnmuteClick,
      )
    }
  }

  handlePlayClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)
    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded'),
    )

    section.setAttribute(
      'data-paused',
      videos.some((el) => !el.video.paused),
    )

    videos.forEach((el) => {
      const video = el.video
      if (video.paused) {
        video.play()
        this.updatePlayPauseButton(true, section)
      } else {
        video.pause()
        this.updatePlayPauseButton(false, section)
      }
    })
  }

  handleUnmuteClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)
    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded'),
    )

    videos.forEach((el) => {
      const video = el.video
      if (video.muted) {
        video.muted = false
        this.updateMuteButton(false, section)
      } else {
        video.muted = true
        this.updateMuteButton(true, section)
      }
    })
  }

  updatePlayPauseButton (isPlaying, section) {
    const buttons = section.querySelectorAll(this.els.play.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isPlaying ? 'false' : 'true')

      const playText = this.querySelector(this.els['play-text'].selector)
      playText.textContent = isPlaying ? this.dataset.pause : this.dataset.play
    })
  }

  updateMuteButton (isMuted, section) {
    const buttons = section.querySelectorAll(this.els.unmute.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isMuted ? 'false' : 'true')

      const muteText = button.querySelector(this.els['unmute-text'].selector)
      if (muteText) {
        muteText.textContent = isMuted
          ? this.dataset.unmute
          : this.dataset.mute
      }
    })
  }
}

if (!customElements.get('lazy-video-controls')) {
  customElements.define('lazy-video-controls', LazyVideoControls)
}
